import { useAuth0 } from "@auth0/auth0-react";
import { Logout, SupportAgentRounded } from '@mui/icons-material';
import { Badge, Box, Button, Divider, Drawer, ListItemIcon, Menu, MenuItem, Stack, SxProps, Theme, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ThemeContext } from "../../contexts";
import { DarkThemeIcon, LeftVector, LightThemeIcon, LogoSVG, LogoSmall, NotificationIcon } from "../../images";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useGetNotificationsQuery } from "../../redux/services";
import { openDrawer } from "../../redux/slices/drawer";
import { toggleBadge } from "../../redux/slices/notifications";
import { setOpenMobileSideNavBar, setOpenWebSideNavBar } from "../../redux/slices/utils";
import { AppRoutes } from "../../router/routes";
import { LocalStorageKeys, Themes, openZohoDeskFeedbackModal, parseJwt, showNotification } from "../../utils";
import { NotificationsMenu } from "../notifications";
import { notificationHeadings } from "../notifications/utils";
import { SideNavBar, navbarWidths } from "./SideNavBar";
import { UserAvatar } from "../userAvatar";

export const tileStyles = (isLight: boolean, hideBg: boolean = false): SxProps<Theme> => ({
    borderRadius: 2,
    cursor: "pointer",
    backgroundColor: (theme) => hideBg ? undefined : isLight ? theme.palette.primary.light : "#F2F3F814",
    border: (theme) => `0.5px solid ${theme.palette.primary.light}`,
    height: 36,
    minWidth: 36,
    fontSize: '0.75rem'
})

export const TopNavBar: React.FC<{ children?: JSX.Element }> = (props) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth0();
    const { showBadge }: any = useAppSelector((state) => state.notifications);
    const { openMobileSideNavBar, openWebSideNavBar } = useAppSelector(store => store.utils.sideNavBar);

    const token = localStorage.getItem(LocalStorageKeys.authToken) || "";

    const data = token ? parseJwt(token) : {};

    const prevTopNotificationTimeRef = useRef<Date | null>(null);

    const { data: allData = { data: [], totalCount: 0 } } =
        useGetNotificationsQuery({ page_no: 1, page_size: 2 }, {
            pollingInterval: 5000,
            skip: !token
        })
    // const [deleteAllNotifications] = useDeleteAllNotificationMutation();

    const themeContext = React.useContext(ThemeContext);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const toogleWebSideNavBar = () => dispatch(setOpenWebSideNavBar(!openWebSideNavBar));

    const toogleMobileSideNavBar = () => dispatch(setOpenMobileSideNavBar(!openMobileSideNavBar));

    const handleProfileClick = (event: React.MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget);

    const handleProfileClose = () => setAnchorEl(null);

    const toggleTheme = () => themeContext.setTheme({ name: themeContext.name === Themes.default ? Themes.dark : Themes.default })

    // const handleDelete = async () => {
    //     dispatch(openBackdrop(`Deleting notifications ...`));
    //     try {
    //         await deleteAllNotifications({}).unwrap();
    //     } catch (error: any) {
    //         let errorMessage: string = error?.data?.title ?? `Unable to delete the notifications!`;
    //         enqueueSnackbar(errorMessage, { variant: "error" });
    //     } finally {
    //         dispatch(closeBackdrop());
    //         dispatch(closeDialog());
    //     }
    // }

    // const initiateDeleteNotifications = () => {
    //     dispatch(
    //         openConfirmationDialog({
    //             title: "Delete Operation",
    //             body: `Are you sure you want to delete all the notifications?`,
    //             positiveBtn: "Delete All",
    //             negativeBtn: "Cancel",
    //             onOk: () => {
    //                 handleDelete();
    //                 dispatch(closeConfirmationDialog());
    //             },
    //             onNegativeBtn: () => dispatch(closeConfirmationDialog()),
    //         })
    //     );
    // };

    const handleNotifications = () => {
        dispatch(openDrawer({
            title: "Notifications",
            component: <NotificationsMenu />,
            onClose: () => dispatch(toggleBadge(false)),
            isLarge: true
        }))
    };

    const handleLogout = () => navigate(AppRoutes.logout);

    const version = localStorage.getItem(LocalStorageKeys.version);
    
    useEffect(() => {
        if (!allData?.data?.length) return;
        
        // Get the latest notification's created time
        const latestNotificationTime = new Date(allData.data[0]?.created_time);
        
        if (!prevTopNotificationTimeRef.current) {
            prevTopNotificationTimeRef.current = latestNotificationTime;
            return;
        }
        
        if (latestNotificationTime > prevTopNotificationTimeRef.current) {
            prevTopNotificationTimeRef.current = latestNotificationTime;
            const message = allData?.data?.[0]?.message || allData?.data?.[0]?.delivery?.in_app?.message || "";
            dispatch(toggleBadge(true));
            enqueueSnackbar(<div style={{ maxWidth: 400 }} dangerouslySetInnerHTML={{ __html: message }} />, {
                anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom',
                }
            })
            showNotification(notificationHeadings[allData?.data?.[0]?.event?.type], message)
        }
        // eslint-disable-next-line
    }, [allData?.data]);

    return (
        (<Stack
            direction={"row"}
            id="topNavBar"
            sx={{
                height: navbarWidths.topNavHeight,
                bgcolor: (theme) => themeContext.name === Themes.default ? theme.palette.background.paper : "#292E32",
                justifyContent: "space-between",
                alignItems: "center",
                pr: 2
            }}>
            <Box
                sx={{
                    display: "flex",
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "center",
                    width: !isMobile && openWebSideNavBar ? navbarWidths.web : navbarWidths.webCollapse,
                    transition: 'all 0.5s'
                }}>
                <NavLink to={AppRoutes.home}>
                    {!isMobile && openWebSideNavBar && <Box
                        sx={{
                            width: 100,
                            height: 36
                        }}>
                        <LogoSVG />
                    </Box>}

                    {(!openWebSideNavBar || isMobile) && <Box
                        sx={{
                            width: 36,
                            height: 36
                        }}>
                        <LogoSmall />
                    </Box>}
                </NavLink>

                <Box
                    onClick={() => isMobile ? toogleMobileSideNavBar() : toogleWebSideNavBar()}
                    sx={{
                        position: "absolute",
                        right: openWebSideNavBar ? -10 : -16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 24,
                        width: 24,
                        border: (t) => `1.5px dashed ${themeContext.name === Themes.default ? "#919EAB33" : "white"}`,
                        borderRadius: "50%",

                        "&:hover": {
                            backgroundColor: (t) => themeContext.name === Themes.default ? t.palette.primary.light : '#F2F3F814'
                        },

                        cursor: "pointer"
                    }}>
                    <LeftVector color={themeContext.name === Themes.default ? '#637381' : 'white'} />
                </Box>
            </Box>
            <Stack
                direction={"row"}
                spacing={1}
                sx={{
                    alignItems: "center",
                    justifyContent: "flex-end"
                }}>
                {/* Report Feedback Icon */}
                <Tooltip title={"Feedback/Report an Issue"}>
                    <Button
                        onClick={openZohoDeskFeedbackModal}
                        sx={tileStyles(themeContext.name === Themes.default)}
                    >
                        <Badge color="error" variant="dot" invisible={false} overlap="circular" >
                            <SupportAgentRounded htmlColor="#637381" />
                        </Badge>
                    </Button>
                </Tooltip>

                {/* Toggle Theme Icon */}
                <Tooltip title={"Toggle Theme"}>
                    <Button
                        onClick={toggleTheme}
                        sx={tileStyles(themeContext.name === Themes.default)}
                    >
                        {themeContext.name === Themes.default && <LightThemeIcon />}
                        {themeContext.name === Themes.dark && <DarkThemeIcon />}
                    </Button>
                </Tooltip>

                {/* Notifications Icon */}
                <Tooltip title={"Notifications"}>
                    <Button
                        onClick={handleNotifications}
                        sx={tileStyles(themeContext.name === Themes.default)}
                    >
                        <Badge color="error" variant="dot" invisible={!showBadge} overlap="circular">
                            <NotificationIcon />
                        </Badge>
                    </Button>
                </Tooltip>

                {/* Profile Pic */}
                <Tooltip title={"View Profile"}>
                    <UserAvatar onClick={handleProfileClick} sx={tileStyles(themeContext.name === Themes.default, true)} userId={`${user?.sub ?? ''}`} name={`${user?.name ?? ""}`} variant="square" />
                </Tooltip>

                {/* Profile Drawer */}
                <Menu
                    id="profile-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleProfileClose}
                    sx={{
                        '.MuiMenu-list': {
                            overflow: "auto"
                        }
                    }}
                >
                    <Box sx={{
                        background: (theme) => theme.palette.grey[100],
                        width: "340px",
                        marginTop: "-8px",
                    }}>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                py: 1
                            }}>
                            <Typography variant="caption">User Name: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{data?.name ?? ""}</Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                pb: 1
                            }}>
                            <Typography variant="caption">Email ID: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{data?.email ?? "NA"}</Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                pb: 1
                            }}>
                            <Typography variant="caption">Organization: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{data?.org_display_name ?? ""}</Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                pb: 1
                            }}>
                            <Typography variant="caption">Plan Type: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{data?.plan ?? "Unknown"}</Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                pb: 1
                            }}>
                            <Typography variant="caption">Role Type: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{data?.role ?? "Unknown"}</Typography>
                        </Stack>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                px: 2,
                                pb: 1
                            }}>
                            <Typography variant="caption">IEHUB Version: </Typography>
                            <Typography sx={{ fontWeight: "bold" }} variant="caption">{`v${version ?? "--"}`}</Typography>
                        </Stack>
                    </Box>

                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout fontSize="small" />
                        </ListItemIcon>
                        Logout
                    </MenuItem>
                </Menu>

                {/* Mobile SideNavBar */}
                <Drawer
                    open={openMobileSideNavBar}
                    variant={"temporary"}
                    anchor="left"
                    onClose={toogleMobileSideNavBar}>
                    <Box style={{ width: navbarWidths.mobile }}>
                        <SideNavBar isMobile={true} />
                    </Box>
                </Drawer>
            </Stack>
        </Stack>)
    );
}
